import React, { useLayoutEffect } from 'react';
import {
  flip,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';

import { Break, Option } from 'kingpin/atoms/ContextMenu';
import { ContextMenuDiv } from 'kingpin/atoms/ContextMenu/styles';
import ContextMenuContext from 'kingpin/atoms/ContextMenu/ContextMenuContext';

import { POPUP_Z_INDEX } from './DataTable.styles';

// A menu entry, ready to draw: the built-in items already resolved to their
// actions
export type MenuEntry =
  | 'separator'
  | {
      label: string;
      onClick?: () => void;
      isDisabled?: boolean;
      subMenu?: MenuEntry[];
    };

const renderEntries = (entries: MenuEntry[]) =>
  entries.map((entry, index) =>
    entry === 'separator' ? (
      <Break key={`separator-${index}`} />
    ) : (
      <Option
        key={`${entry.label}-${index}`}
        label={entry.label}
        onClick={entry.onClick}
        isDisabled={entry.isDisabled}
        subMenu={
          entry.subMenu ? <>{renderEntries(entry.subMenu)}</> : undefined
        }
      />
    ),
  );

// A table's right-click menu (AG's context menu), opened where the pointer
// is. Pressing outside it or Escape closes it, as does choosing an item.
const DataTableContextMenu = ({
  x,
  y,
  entries,
  onClose,
}: {
  x: number;
  y: number;
  entries: MenuEntry[];
  onClose: () => void;
}) => {
  const { refs, floatingStyles, context } = useFloating({
    open: true,
    onOpenChange: (isOpen) => {
      if (!isOpen) {
        onClose();
      }
    },
    placement: 'right-start',
    strategy: 'fixed',
    middleware: [offset(2), flip(), shift({ padding: 8 })],
  });
  useLayoutEffect(() => {
    refs.setPositionReference({
      getBoundingClientRect: () => ({
        x,
        y,
        top: y,
        left: x,
        right: x,
        bottom: y,
        width: 0,
        height: 0,
      }),
    });
  }, [refs, x, y]);
  const dismiss = useDismiss(context);
  const { getFloatingProps } = useInteractions([dismiss]);

  return (
    <FloatingPortal>
      <div
        ref={refs.setFloating}
        style={{ ...floatingStyles, zIndex: POPUP_Z_INDEX }}
        role="menu"
        data-testid="data-table-context-menu"
        {...getFloatingProps()}
      >
        <ContextMenuContext.Provider
          value={{ closeMenu: onClose, hideMenu: onClose }}
        >
          <ContextMenuDiv>{renderEntries(entries)}</ContextMenuDiv>
        </ContextMenuContext.Provider>
      </div>
    </FloatingPortal>
  );
};

export default DataTableContextMenu;
